import {
  DEFAULT_QUOTE_VALIDITY_DAYS,
  DEFAULT_TIER_UOM_ID,
  toIsoDate,
} from "../components/NewRequestDialog/pricing/constants"
import type { PricingFormValues, PricingTierFormValue } from "../components/NewRequestDialog/pricing/schemas"
import { PricingPath } from "../components/NewRequestDialog/pricing/types"
import type { NewRequestFormValues } from "../types"

import type {
  Incoterms,
  ProductQuoteInput,
  ProductRequestInput,
  ProductSpecialTierInput,
  SalesRequestCreateInput,
} from "@/graphql/codegen/graphql"
import { QuotePricingPath } from "@/graphql/codegen/graphql"

interface ResolvedDateWindow {
  effectiveDate: string
  validUntilDate: string | undefined
}

// Per PO: pre-fill is today / today+14 days. If the user clears both fields, the
// serializer falls back to that same window so the BE always sees valid dates.
const resolveDateWindow = (effective: string, validUntil: string | null): ResolvedDateWindow => {
  if (!effective && !validUntil) {
    const today = new Date()
    const validTo = new Date(today)
    validTo.setDate(today.getDate() + DEFAULT_QUOTE_VALIDITY_DAYS)
    return { effectiveDate: toIsoDate(today), validUntilDate: toIsoDate(validTo) }
  }
  return { effectiveDate: effective, validUntilDate: validUntil ?? undefined }
}

interface ToSalesRequestCreateInputOptions {
  emailReceiverContactIds: string[]
  emailNote?: string | null
}

export const toSalesRequestCreateInput = (
  formValues: NewRequestFormValues,
  pricingByProductId: Map<number, PricingFormValues>,
  options: ToSalesRequestCreateInputOptions
): SalesRequestCreateInput => {
  const products = (formValues.products ?? [])
    .filter((added) => added.requestQuote)
    .map<ProductRequestInput>((added) => {
      const pricing = pricingByProductId.get(added.product.productId)
      if (!pricing) {
        throw new Error(`Missing pricing for product ${added.product.productId}`)
      }
      return { productId: added.product.productId, quote: toProductQuoteInput(pricing) }
    })

  const trimmedNote = options.emailNote?.trim()

  return {
    customerId: formValues.customer.billingLocation.locationId,
    requesterContactIds: formValues.requesters.map((requester) => requester.contactId),
    emailReceiverContactIds: options.emailReceiverContactIds,
    emailNote: trimmedNote ? trimmedNote : undefined,
    products,
  }
}

const toProductQuoteInput = (pricing: PricingFormValues): ProductQuoteInput => {
  if (pricing.path === PricingPath.List) {
    return {
      path: QuotePricingPath.List,
      list: {
        shipFromWarehouseAreaId: pricing.shipFromLocationId,
        // FE Zod stores incoterms as a string; values come from the Incoterms enum dropdown so
        // the cast is safe at runtime.
        incoterms: pricing.incoterms as Incoterms,
        incotermsLocationId: pricing.incotermsLocationId,
        estimatedAnnualVolume: pricing.estimatedAnnualVolume ?? undefined,
      },
    }
  }
  const dateWindow = resolveDateWindow(pricing.effectiveDate, pricing.validUntilDate)
  return {
    path: QuotePricingPath.Special,
    special: {
      estimatedAnnualVolume: pricing.estimatedAnnualVolume ?? undefined,
      pricePoint: pricing.pricePoint ? Number(pricing.pricePoint) : undefined,
      incoterms: pricing.incoterms,
      incotermsLocationId: pricing.incotermsLocationId,
      shipFromWarehouseAreaIds: pricing.shipFromLocationIds,
      freightTerms: pricing.freightTerms,
      effectiveDate: dateWindow.effectiveDate,
      validUntilDate: dateWindow.validUntilDate,
      tiers: toTierInputs(pricing.tiers),
    },
  }
}

// Skip tier rows where qty is null/missing. When all tiers are blank send a single
// placeholder row so the BE always receives at least one tier.
const toTierInputs = (tiers: PricingTierFormValue[]): ProductSpecialTierInput[] => {
  const filled = tiers
    .filter((tier) => tier.qty !== null)
    .map((tier) => ({
      qty: tier.qty!,
      uomId: tier.uom,
      price: tier.price ? Number(tier.price) : undefined,
    }))
  return filled.length > 0 ? filled : [{ qty: 1, uomId: DEFAULT_TIER_UOM_ID, price: 0 }]
}
